// Rejestracja panelu w FCM (Web Push) — powiadomienia, gdy przeglądarka jest zamknięta
// albo karta uśpiona. Dźwięk zostaje po stronie aplikacji (`useNotificationAlerts`):
// powiadomienie z service workera gra wyłącznie systemowy dźwięk przeglądarki i nie da
// się do niego podpiąć własnego pliku.

import { arrayRemove, arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { deleteToken, getMessaging, getToken, isSupported } from 'firebase/messaging';

import { db, firebaseApp } from '@/config';

/** Klucz publiczny VAPID z konsoli Firebase (Cloud Messaging → Web configuration). */
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
/** Token trzymamy lokalnie, żeby przy wylogowaniu wiedzieć, co usunąć z profilu. */
const TOKEN_STORAGE_KEY = 'hs.webPushToken';

export type PushPermission = 'granted' | 'denied' | 'default' | 'unsupported';

/**
 * Stan zgody CZYTAMY, nie pytamy — `Notification.permission` jest synchroniczne i nie
 * pokazuje żadnego monitu. Dzięki temu panel wie po wejściu, czy ma sens pokazać własną
 * zachętę, czy raczej instrukcję odblokowania w ustawieniach przeglądarki.
 */
export const getPushPermission = (): PushPermission => {
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) return 'unsupported';
  return Notification.permission as PushPermission;
};

export const isPushConfigured = (): boolean => !!VAPID_KEY;

/**
 * Monit przeglądarki. MUSI wyjść z gestu użytkownika i realnie pojawia się tylko przy
 * stanie `default` — po „Blokuj" przeglądarka rozstrzyga natychmiast `denied` i nic nie
 * wyświetla. Dlatego panel nigdy nie woła tego automatycznie po zalogowaniu: jedno
 * odruchowe kliknięcie wycisza domenę na stałe, bez drogi powrotnej z poziomu kodu.
 */
export const requestPushPermission = async (): Promise<PushPermission> => {
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    return (await Notification.requestPermission()) as PushPermission;
  } catch {
    return getPushPermission();
  }
};

/**
 * Pokazuje powiadomienie systemowe „z tej karty".
 *
 * NAJPIERW przez service workera, dopiero potem konstruktorem `new Notification()`:
 * Chrome na Androidzie konstruktora ZABRANIA (rzuca „Illegal constructor. Use
 * ServiceWorkerRegistration.showNotification() instead"), a i na desktopie dymek
 * wystawiony przez workera zachowuje się tak samo jak ten z pusha — więc `tag`
 * poprawnie podmienia powiadomienie zamiast dublować.
 *
 * Zwraca `false`, gdy nie udało się nic wyświetlić — wołający ma wtedy co pokazać
 * użytkownikowi zamiast ciszy.
 */
export const showLocalNotification = async (
  title: string,
  options: NotificationOptions,
): Promise<boolean> => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;

  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    if (registration?.showNotification) {
      await registration.showNotification(title, options);
      return true;
    }
  } catch {
    // Worker niedostępny albo zablokowany — próbujemy konstruktorem.
  }

  try {
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
    return true;
  } catch {
    return false;
  }
};

/** Rejestracja workera z konfiguracją projektu w query stringu (worker nie widzi env-ów). */
const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) return null;

  const config = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  } as Record<string, string | undefined>;

  const params = new URLSearchParams();
  Object.entries(config).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });

  try {
    return await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params.toString()}`);
  } catch {
    return null;
  }
};

/**
 * Zapisuje token urządzenia na profilu użytkownika.
 *
 * Trafia do tej samej tablicy `fcmTokens`, z której backend wysyła pushe na telefony —
 * wysyłka nie wymaga więc żadnej zmiany po stronie funkcji. Token jest per profil
 * przeglądarki, więc jedna osoba na trzech komputerach ma trzy wpisy.
 */
export const registerWebPushToken = async (userId: string): Promise<string | null> => {
  if (!VAPID_KEY || getPushPermission() !== 'granted') return null;
  if (!(await isSupported().catch(() => false))) return null;

  const registration = await registerServiceWorker();
  if (!registration) return null;

  try {
    const token = await getToken(getMessaging(firebaseApp), {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) return null;

    await updateDoc(doc(db, 'users', userId), { fcmTokens: arrayUnion(token) });
    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch {
      // Brak localStorage — token zostanie posprzątany dopiero przy kolejnym logowaniu.
    }
    return token;
  } catch {
    return null;
  }
};

/**
 * Sprząta token przy wylogowaniu.
 *
 * Bez tego na współdzielonym komputerze recepcji powiadomienia dla poprzedniej zmiany
 * lądują na profilu, przy którym siedzi już ktoś inny.
 */
export const unregisterWebPushToken = async (userId: string): Promise<void> => {
  let token: string | null = null;
  try {
    token = localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    token = null;
  }
  if (!token) return;

  try {
    await updateDoc(doc(db, 'users', userId), { fcmTokens: arrayRemove(token) });
  } catch {
    // Profil mógł zniknąć — token i tak wygaśnie po stronie FCM.
  }
  try {
    await deleteToken(getMessaging(firebaseApp));
  } catch {
    // Token mógł już zostać unieważniony przez przeglądarkę.
  }
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // nic
  }
};
